// Single Zustand store managing:
//   - the WebSocket transport lifecycle
//   - per-agent chat history (loaded lazily from IndexedDB)
//   - per-agent unread counts
//   - active agent selection
//   - streaming delta buffers (ephemeral; collapsed when complete frame arrives)
//   - status / error / question prompt state per agent
//   - optimistic outbound user messages with delivery status
//
// Components subscribe to slices of this store via shallow comparators.

import { create } from 'zustand';
import { shallow } from 'zustand/shallow';
import {
  emptyHistory,
  listAgentIds,
  loadHistory,
  scheduleSave,
  type ChatHistory,
} from '../persistence/db';
import {
  WebSocketClient,
  type ConnectionState,
  uploadFile,
  downloadAttachment,
  NiuUploadError,
} from '../transport';
import type {
  AgentDescriptor,
  Attachment,
  AssistantMessageDeltaPayload,
  AssistantMessagePayload,
  ErrorPayload,
  NiuMessage,
  QuestionPayload,
  ReactionPayload,
  StatusPayload,
  ThinkingDeltaPayload,
  ThinkingPayload,
  ToolCallDeltaPayload,
  ToolCallPayload,
  ToolResultPayload,
  UserMessagePayload,
  DeviceInfo,
  InlineSnippetPayload,
  RateLimitPayload,
} from '../protocol/types';
import { payloadAgentId } from '../protocol/types';
import { useSettingsStore } from './settingsStore';
import { clientMessageId, newUUID } from './uuid';

// ---------- Types exported to UI ----------

export interface StreamingBlock {
  key: string; // `${messageId}|${blockIndex}`
  kind: 'assistant' | 'thinking' | 'toolCall';
  messageId: string;
  blockIndex: number;
  /** Full content received from the server so far. */
  buffer: string;
  /** How many characters of `buffer` are currently rendered. The rAF tick
   *  advances this toward `buffer.length` to smooth out server burstiness
   *  (perceived "chunky" streaming when the server flushes 8 words at once). */
  displayedLen: number;
  /** Number of server arrivals seen so far. ≥2 means we have enough data
   *  to compute a meaningful chars/sec rate. */
  chunkCount: number;
  /** Fractional residual from the last tick — chars accumulated but not yet
   *  enough to advance by a whole character. */
  revealResidual: number;
  toolUseId?: string;
  startedAt: number;
}

export interface AgentChat {
  agentId: string;
  history: ChatHistory;
  status: StatusPayload | null;
  pendingQuestion: QuestionPayload | null;
  lastError: ErrorPayload | null;
  streamingBlocks: Record<string, StreamingBlock>;
  /** `toolUseId`s of `tool_call` complete frames seen this turn. A late
   *  `tool_call_delta` carrying one of these is dropped — otherwise
   *  `appendDelta` would resurrect an orphan in the streaming preview.
   *  Cleared on idle status, disconnect, and agent switch. */
  finalizedToolUseIds: Set<string>;
  loaded: boolean;
  /** True while a `fetch_earlier_messages` request is in flight (§3.11). */
  loadingOlder: boolean;
  /** Server confirmed nothing older remains (§4.14 `more=false` or §1.2 gap). */
  reachedOldestRestored: boolean;
  /** Active inline snippet — the agent's live split-pane view, or null. Never
   *  persisted: it dies with the agent's container, so resurrecting one from
   *  IndexedDB would only show a dead iframe. */
  inlineSnippet: InlineSnippetPayload | null;
  /** Bumped by each `inline_snippet_reload` frame so the panel can remount
   *  the iframe. */
  inlineSnippetReloadNonce: number;
  /** Last provider usage snapshot (codex rate limits), or null. Transient —
   *  never persisted; re-sent by the host on connect, refreshed each turn.
   *  The sidebar hides it once `updatedAt` is older than an hour. */
  rateLimit: RateLimitPayload | null;
}

export type SendState = 'idle' | 'sending' | 'connecting';

// Special agent id used when the server publishes ≤1 agent (no `agent` field
// on payloads) — keeps the per-agent logic uniform.
export const DEFAULT_AGENT_ID = '__default__';

// ---------- Internal store shape ----------

interface ChatStoreState {
  connection: ConnectionState;
  /** Agents the server says we can address, in advertise order. */
  agents: AgentDescriptor[];
  activeAgentId: string;
  chats: Record<string, AgentChat>;
  /** Per-agent unread count, increments when not the active agent. */
  unread: Record<string, number>;
  /** Roster of devices known to the bucket. Populated from `devices_list`
   *  frames; refreshed automatically on connect and after a rename/delete. */
  knownDevices: DeviceInfo[];
  /** True between a `devices_list_request` send and the matching response. */
  devicesLoading: boolean;
  /** Anything destined for an unknown agent goes here until `agents_available`. */
  // (no orphan buffer — we just create a chat entry on demand)

  // Internal:
  client: WebSocketClient | null;
  /** Outbound user messages awaiting an inbound echo (by content hash). */
  inflightOutbound: Map<string, { agentId: string; clientId: string }>;
}

interface ChatStoreActions {
  connect: () => void;
  disconnect: () => void;
  setActiveAgent: (agentId: string) => void;
  ensureAgentChat: (agentId: string) => Promise<void>;
  sendUserMessage: (text: string, attachments?: Attachment[]) => Promise<void>;
  retryUserMessage: (clientId: string) => Promise<void>;
  answerQuestion: (questionId: string, selectedOption: string) => void;
  uploadAttachment: (file: File, onProgress?: (f: number) => void) => Promise<Attachment>;
  fetchAttachmentBlobURL: (attachmentURL: string) => Promise<string>;
  clearError: (agentId: string) => void;
  requestDevicesList: () => void;
  deleteDevice: (deviceId: string) => void;
  /** Set the manual override name on a device. Empty / whitespace clears it
   *  and the server falls back to the system-supplied name. */
  renameDevice: (deviceId: string, deviceName: string) => void;
  /** Report whether the user is actively looking at this tab. Drives
   *  APNs sibling-suppression — an `active` web tab silences pushes to
   *  the user's other devices. No-op when no socket is open. */
  reportPresence: (state: 'active' | 'inactive') => void;
  /** Page backward through the durable log (§3.11). */
  loadOlderMessages: (agentId: string, limit?: number) => void;
  /** Dismiss the inline-snippet panel locally. The agent's container keeps
   *  running — this is a UI close, not a `close_inline_snippet`. */
  closeInlineSnippet: (agentId: string) => void;
}

type Store = ChatStoreState & ChatStoreActions;

// ---------- Helpers ----------

function ensureChat(s: ChatStoreState, agentId: string): AgentChat {
  if (!s.chats[agentId]) {
    s.chats[agentId] = {
      agentId,
      history: emptyHistory(),
      status: null,
      pendingQuestion: null,
      lastError: null,
      streamingBlocks: {},
      finalizedToolUseIds: new Set(),
      loaded: false,
      loadingOlder: false,
      reachedOldestRestored: false,
      inlineSnippet: null,
      inlineSnippetReloadNonce: 0,
      rateLimit: null,
    };
  }
  return s.chats[agentId]!;
}

/** Hash a user-message body well enough to dedupe an inbound echo.
 *  Intentionally excludes `agent` — protocol §3.1 rule 3 lets the server attach
 *  a resolved selector even when the client omitted it, so the hashes would
 *  differ and the echo would not be recognised as a duplicate. */
function userMessageHash(p: UserMessagePayload): string {
  const att = (p.attachments ?? []).map((a) => `${a.id}:${a.size}`).join(',');
  return `${p.text}|${att}`;
}

function streamingKey(messageId: string, blockIndex: number): string {
  return `${messageId}|${blockIndex}`;
}

function appendDelta(
  blocks: Record<string, StreamingBlock>,
  kind: StreamingBlock['kind'],
  messageId: string,
  blockIndex: number,
  chunk: string,
  toolUseId?: string,
): Record<string, StreamingBlock> {
  const k = streamingKey(messageId, blockIndex);
  const existing = blocks[k];
  const next: Record<string, StreamingBlock> = { ...blocks };
  const now = Date.now();
  if (existing) {
    next[k] = {
      ...existing,
      buffer: existing.buffer + chunk,
      chunkCount: existing.chunkCount + 1,
    };
  } else {
    const block: StreamingBlock = {
      key: k,
      kind,
      messageId,
      blockIndex,
      buffer: chunk,
      displayedLen: 0,
      chunkCount: 1,
      revealResidual: 0,
      startedAt: now,
    };
    if (toolUseId !== undefined) block.toolUseId = toolUseId;
    next[k] = block;
  }
  return next;
}

/** Parse ms timestamp from niu-<unix_ms>-<rand6> IDs. Returns null for client UUIDs. */
function niuIdTimestamp(id: string): number | null {
  if (!id.startsWith('niu-')) return null;
  const parts = id.split('-');
  if (parts.length < 3) return null;
  const ms = parseInt(parts[1], 10);
  return isNaN(ms) ? null : ms;
}

/** For late-arriving aux frames (toolResult/toolCall/thinking), walk back past
 *  trailing assistant_messages that belong to the same turn (within 30 s).
 *  Mirrors Swift's ChatViewModel.reorderIndexForLateAuxFrame. */
function reorderIndexForLateAuxFrame(
  incoming: NiuMessage,
  messages: NiuMessage[],
  timestamps: Record<string, number>,
  fallback: number,
): number {
  const kind = incoming.payload.kind;
  if (kind !== 'tool_result' && kind !== 'tool_call' && kind !== 'thinking') return fallback;

  const incomingTs = niuIdTimestamp(incoming.id) ?? Date.now();
  let i = fallback;
  while (i > 0) {
    const prev = messages[i - 1]!;
    if (prev.payload.kind !== 'assistant_message') break;
    const prevTs = timestamps[prev.id];
    if (prevTs === undefined) break;
    if (incomingTs - prevTs > 30_000) break;
    i -= 1;
  }
  return i;
}

function collapseStreaming(
  blocks: Record<string, StreamingBlock>,
  kind: StreamingBlock['kind'],
): Record<string, StreamingBlock> {
  const out: Record<string, StreamingBlock> = {};
  for (const [k, v] of Object.entries(blocks)) if (v.kind !== kind) out[k] = v;
  return out;
}

/** Drop only the toolCall block(s) sharing this `toolUseId`. Used when a
 *  complete `tool_call` frame arrives — leaves any sibling tool_call still
 *  streaming under a different `toolUseId` intact. Pair with
 *  `finalizedToolUseIds` so a late delta can be dropped instead of
 *  resurrecting an orphan. */
function collapseToolCallByUseId(
  blocks: Record<string, StreamingBlock>,
  toolUseId: string,
): Record<string, StreamingBlock> {
  const out: Record<string, StreamingBlock> = {};
  for (const [k, v] of Object.entries(blocks)) {
    if (!(v.kind === 'toolCall' && v.toolUseId === toolUseId)) out[k] = v;
  }
  return out;
}

// ---------- Store ----------

/** Monotonic connect() id. connect() awaits IndexedDB before opening its
 *  socket; it re-checks this afterward so a newer connect() (rapid reconnect /
 *  reload) supersedes a stale one instead of leaving two sockets racing. */
let connectGen = 0;

export const useChatStore = create<Store>((set, get) => ({
  connection: { kind: 'disconnected' },
  agents: [],
  activeAgentId: DEFAULT_AGENT_ID,
  chats: {},
  unread: {},
  knownDevices: [],
  devicesLoading: false,
  client: null,
  inflightOutbound: new Map(),

  connect: () => {
    const { wssUrl, token, deviceId } = useSettingsStore.getState();
    if (!wssUrl || !token) return;

    // Tear down any existing client before the async history load below so a
    // rapid reconnect / reload can't leave two sockets live at once.
    get().client?.disconnect();
    const myGen = ++connectGen;

    // Load every persisted agent history first, then open the socket with a
    // `since_seq` derived from the highest seq we hold *durably in IndexedDB*.
    // There is no separately-persisted cursor: one could advance past message
    // writes still sitting in the 200ms save debounce — a page reload before
    // it flushes — after which the server, believing us current, never
    // replays the gap. Deriving the cursor from the stored history makes that
    // desync impossible: we only ever ask for what we don't already have.
    void (async () => {
      const ids = await listAgentIds();
      const histories: Record<string, ChatHistory> = {};
      let sinceSeq = 0;
      for (const id of ids) {
        const h = await loadHistory(id);
        histories[id] = h;
        for (const m of h.messages) {
          if (m.seq != null && m.seq > sinceSeq) sinceSeq = m.seq;
        }
      }
      // A newer connect() superseded this one while we awaited IndexedDB.
      if (myGen !== connectGen) return;

      set((s) => {
        const chats = { ...s.chats };
        for (const id of ids) {
          const base = ensureChat({ ...s, chats }, id);
          chats[id] = { ...base, history: histories[id]!, loaded: true };
        }
        let activeAgentId = s.activeAgentId;
        if (ids.length > 0) {
          const lastActive = useSettingsStore.getState().loadActiveAgent();
          activeAgentId = lastActive && ids.includes(lastActive) ? lastActive : ids[0]!;
        }
        return { chats, activeAgentId };
      });

      const client = new WebSocketClient({
        url: wssUrl,
        token,
        deviceId,
        sinceSeq,
        onMessage: (msg) => handleInbound(msg, set, get),
        onState: (state) => {
          if (state.kind === 'disconnected' || state.kind === 'error') {
            // The socket dropped out from under us. `status` frames aren't
            // catch-up replayed (niu-ws-protocol §4.11), so a stale
            // "Using Tools…" would otherwise stick until reload. Clear status
            // and in-flight delta previews across every chat — same reasoning
            // as the explicit `disconnect()` action.
            flushPendingDeltas();
            set((s) => {
              const chats: Record<string, AgentChat> = {};
              for (const [id, chat] of Object.entries(s.chats)) {
                chats[id] = { ...chat, status: null, streamingBlocks: {}, finalizedToolUseIds: new Set() };
              }
              return { connection: state, chats };
            });
            return;
          }
          set({ connection: state });
          if (state.kind === 'connected') {
            // Web has no APNs path — `device_info` is the only metadata frame
            // the server gets, and it must arrive on every connect so the
            // Settings → Devices roster shows the browser correctly.
            sendDeviceInfo(get);
            // The server seeds every fresh socket as `inactive`, so re-assert
            // the tab's real visibility on each connect — otherwise an open,
            // focused tab wouldn't suppress pushes to the user's phone.
            sendPresence(get, visibilityPresence());
            // Refresh the device list on connect so Settings opens with live data.
            get().requestDevicesList();
          }
        },
        onDecodeFailure: (err, raw) => {
          // eslint-disable-next-line no-console
          console.warn('[niu] schema drift', err.message, raw.slice(0, 300));
        },
      });
      set({ client });
      client.connect();
    })();
  },

  disconnect: () => {
    flushPendingDeltas();
    get().client?.disconnect();
    // Drop any in-flight streaming preview — deltas aren't catch-up
    // replayed (niu-ws-protocol §4.11), so anything still buffered would
    // become a permanent stub if the matching complete frame already
    // passed before disconnect.
    set((s) => {
      const chats: Record<string, AgentChat> = {};
      for (const [id, chat] of Object.entries(s.chats)) {
        chats[id] = { ...chat, streamingBlocks: {}, finalizedToolUseIds: new Set() };
      }
      return { client: null, connection: { kind: 'disconnected' }, chats };
    });
  },

  setActiveAgent: (agentId) => {
    flushPendingDeltas();
    set((s) => ({
      activeAgentId: agentId,
      unread: { ...s.unread, [agentId]: 0 },
    }));
    useSettingsStore.getState().saveActiveAgent(agentId);
    void get().ensureAgentChat(agentId);
  },

  ensureAgentChat: async (agentId) => {
    const existing = get().chats[agentId];
    if (existing?.loaded) return;
    // Optimistically seed with empty history so the UI can render.
    set((s) => {
      const next = { ...s.chats };
      const chat = next[agentId] ?? {
        agentId,
        history: emptyHistory(),
        status: null,
        pendingQuestion: null,
        lastError: null,
        streamingBlocks: {},
        finalizedToolUseIds: new Set<string>(),
        loaded: false,
      };
      next[agentId] = chat;
      return { chats: next };
    });
    const history = await loadHistory(agentId);
    set((s) => {
      const next = { ...s.chats };
      const chat = next[agentId];
      if (!chat) return s;
      next[agentId] = { ...chat, history, loaded: true };
      return { chats: next };
    });
  },

  sendUserMessage: async (text, attachments) => {
    const trimmed = text.trim();
    if (!trimmed && (!attachments || attachments.length === 0)) return;
    const { client, agents, activeAgentId } = get();
    if (!client) return;

    // Emit `agent` selector only when there's >1 wired agent (server falls
    // back to default routing when absent).
    const agentSelector = agents.length > 1 && activeAgentId !== DEFAULT_AGENT_ID
      ? activeAgentId
      : undefined;

    const id = clientMessageId();
    const payload: UserMessagePayload = { text: trimmed };
    if (attachments && attachments.length) payload.attachments = attachments;
    if (agentSelector !== undefined) payload.agent = agentSelector;
    const message: NiuMessage = {
      id,
      type: 'user_message',
      payload: { kind: 'user_message', data: payload },
    };

    // Optimistically append to the active chat with `sending` status.
    const targetAgentId = agentSelector ?? activeAgentId;
    set((s) => {
      const chats = { ...s.chats };
      const chat = ensureChat({ ...s, chats }, targetAgentId);
      const history: ChatHistory = {
        ...chat.history,
        messages: [...chat.history.messages, message],
        timestamps: { ...chat.history.timestamps, [id]: Date.now() },
        deliveryStatus: { ...chat.history.deliveryStatus, [id]: 'sending' },
      };
      chats[targetAgentId] = { ...chat, history };
      const inflight = new Map(s.inflightOutbound);
      inflight.set(userMessageHash(payload), { agentId: targetAgentId, clientId: id });
      scheduleSave(targetAgentId, history);
      return { chats, inflightOutbound: inflight };
    });

    const ok = client.send(message);
    set((s) => {
      const chats = { ...s.chats };
      const chat = chats[targetAgentId];
      if (!chat) return s;
      const history: ChatHistory = {
        ...chat.history,
        deliveryStatus: {
          ...chat.history.deliveryStatus,
          [id]: ok ? 'sent' : 'failed',
        },
      };
      chats[targetAgentId] = { ...chat, history };
      scheduleSave(targetAgentId, history);
      return { chats };
    });
  },

  retryUserMessage: async (clientId) => {
    const { client, chats } = get();
    if (!client) return;
    let resentMessage: NiuMessage | null = null;
    let agentId: string | null = null;
    for (const [aid, chat] of Object.entries(chats)) {
      const m = chat.history.messages.find((x) => x.id === clientId);
      if (m) {
        resentMessage = m;
        agentId = aid;
        break;
      }
    }
    if (!resentMessage || !agentId) return;
    set((s) => {
      const chats2 = { ...s.chats };
      const chat = chats2[agentId!];
      if (!chat) return s;
      const history: ChatHistory = {
        ...chat.history,
        deliveryStatus: { ...chat.history.deliveryStatus, [clientId]: 'sending' },
      };
      chats2[agentId!] = { ...chat, history };
      scheduleSave(agentId!, history);
      return { chats: chats2 };
    });
    const ok = client.send(resentMessage);
    set((s) => {
      const chats2 = { ...s.chats };
      const chat = chats2[agentId!];
      if (!chat) return s;
      const history: ChatHistory = {
        ...chat.history,
        deliveryStatus: {
          ...chat.history.deliveryStatus,
          [clientId]: ok ? 'sent' : 'failed',
        },
      };
      chats2[agentId!] = { ...chat, history };
      scheduleSave(agentId!, history);
      return { chats: chats2 };
    });
  },

  answerQuestion: (questionId, selectedOption) => {
    const { client, activeAgentId } = get();
    if (!client) return;
    const id = clientMessageId();
    client.send({
      id,
      type: 'answer',
      payload: { kind: 'answer', data: { questionId, selectedOption } },
    });
    // Lock the question card by recording the answer locally.
    set((s) => {
      const chats = { ...s.chats };
      const chat = chats[activeAgentId];
      if (!chat) return s;
      const history: ChatHistory = {
        ...chat.history,
        answers: { ...chat.history.answers, [questionId]: selectedOption },
      };
      chats[activeAgentId] = {
        ...chat,
        history,
        pendingQuestion:
          chat.pendingQuestion?.questionId === questionId ? null : chat.pendingQuestion,
      };
      scheduleSave(activeAgentId, history);
      return { chats };
    });
  },

  uploadAttachment: async (file, onProgress) => {
    const { wssUrl, token } = useSettingsStore.getState();
    if (!wssUrl || !token) {
      throw new NiuUploadError({ kind: 'transport', message: 'no credentials' });
    }
    const opts: Parameters<typeof uploadFile>[0] = {
      webSocketURL: wssUrl,
      token,
      file,
      filename: file.name,
      mimeType: file.type || 'application/octet-stream',
    };
    if (onProgress) opts.onProgress = onProgress;
    return uploadFile(opts);
  },

  fetchAttachmentBlobURL: async (attachmentURL) => {
    const { wssUrl, token } = useSettingsStore.getState();
    const blob = await downloadAttachment({ webSocketURL: wssUrl, token, attachmentURL });
    return URL.createObjectURL(blob);
  },

  clearError: (agentId) =>
    set((s) => {
      const chats = { ...s.chats };
      const chat = chats[agentId];
      if (!chat) return s;
      chats[agentId] = { ...chat, lastError: null };
      return { chats };
    }),

  closeInlineSnippet: (agentId) =>
    set((s) => {
      const chat = s.chats[agentId];
      if (!chat || !chat.inlineSnippet) return s;
      return { chats: { ...s.chats, [agentId]: { ...chat, inlineSnippet: null } } };
    }),

  requestDevicesList: () => {
    const { client } = get();
    if (!client) {
      set({ devicesLoading: true });
      return;
    }
    set({ devicesLoading: true });
    const ok = client.send({
      id: clientMessageId(),
      type: 'devices_list_request',
      payload: { kind: 'devices_list_request', data: {} },
    });
    if (!ok) set({ devicesLoading: false });
  },

  deleteDevice: (deviceId) => {
    const { client } = get();
    if (!client) return;
    client.send({
      id: clientMessageId(),
      type: 'device_delete_request',
      payload: { kind: 'device_delete_request', data: { deviceId } },
    });
  },

  renameDevice: (deviceId, deviceName) => {
    const { client } = get();
    if (!client) return;
    client.send({
      id: clientMessageId(),
      type: 'device_rename_request',
      payload: {
        kind: 'device_rename_request',
        data: { deviceId, deviceName: deviceName.trim() },
      },
    });
  },

  reportPresence: (state) => {
    sendPresence(get, state);
  },

  loadOlderMessages: (agentId, limit = 200) => {
    const state = get();
    const chat = state.chats[agentId];
    if (!chat || chat.loadingOlder || chat.reachedOldestRestored) return;
    const lowestSeq = chat.history.messages.reduce<number | null>((min, m) => {
      if (m.seq == null) return min;
      return min == null || m.seq < min ? m.seq : min;
    }, null);
    if (lowestSeq == null) return;
    const ok = state.client?.fetchEarlier(lowestSeq, limit) ?? false;
    if (!ok) return;
    set((s) => {
      const next = { ...s.chats };
      const c = next[agentId];
      if (c) next[agentId] = { ...c, loadingOlder: true };
      return { chats: next };
    });
  },
}));

/** Build + send the `device_info` frame. Called from the `connect` action
 *  on every successful socket open so the server's `niu_known_devices`
 *  row carries platform / version / locale / browser-derived name even
 *  though browsers have no APNs equivalent. */
function sendDeviceInfo(get: () => Store): void {
  const { client } = get();
  if (!client) return;
  const { deviceId } = useSettingsStore.getState();
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const locale = typeof navigator !== 'undefined' ? navigator.language : undefined;
  const appVersion = APP_VERSION;
  const bundleId = APP_BUNDLE_ID;
  const deviceName = deriveBrowserDeviceName(ua);
  client.send({
    id: clientMessageId(),
    type: 'device_info',
    payload: {
      kind: 'device_info',
      data: {
        deviceId,
        platform: 'web',
        bundleId,
        appVersion,
        locale,
        deviceName,
      },
    },
  });
}

/** Browser visibility → presence state. `inactive` only when the tab is
 *  genuinely hidden (background tab / minimized). Guarded so it stays safe
 *  outside a DOM (tests). */
function visibilityPresence(): 'active' | 'inactive' {
  return typeof document !== 'undefined' && document.hidden ? 'inactive' : 'active';
}

/** Send a `presence` frame (niu-ws-protocol §3.5). The server uses it to
 *  suppress APNs alerts to the user's other devices while they're reading
 *  here — an `active` web tab means "don't ping my phone". No-op when no
 *  socket is open; presence is re-asserted on the next connect. */
function sendPresence(get: () => Store, state: 'active' | 'inactive'): void {
  const { client } = get();
  if (!client) return;
  client.send({
    id: clientMessageId(),
    type: 'presence',
    payload: { kind: 'presence', data: { state } },
  });
}

const APP_VERSION = (import.meta.env?.VITE_APP_VERSION as string | undefined) ?? '0.0.0-dev';
const APP_BUNDLE_ID = 'com.fabianwirth.niu.web';

/** Parse a friendly "Browser on Platform" label from the User-Agent string.
 *  Imprecise on purpose — we just need *something* better than "Unknown"
 *  in the Settings → Devices list. */
function deriveBrowserDeviceName(ua: string): string {
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser';
  let os = 'Unknown OS';
  if (/Windows NT/.test(ua)) os = 'Windows';
  else if (/Mac OS X|Macintosh/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';
  return `${browser} on ${os}`;
}

// ---------- Inbound dispatch ----------

function handleInbound(
  msg: NiuMessage,
  set: (fn: (s: Store) => Partial<Store> | Store) => void,
  get: () => Store,
): void {
  const agentId = payloadAgentId(msg.payload) ?? get().activeAgentId ?? DEFAULT_AGENT_ID;

  switch (msg.payload.kind) {
    case 'agents_available': {
      const agents = msg.payload.data.agents;
      set((s) => {
        let active = s.activeAgentId;
        if (active === DEFAULT_AGENT_ID || !agents.find((a) => a.id === active)) {
          active = agents[0]?.id ?? DEFAULT_AGENT_ID;
        }
        return { agents, activeAgentId: active };
      });
      // Lazy-load history for known agents.
      for (const a of agents) void get().ensureAgentChat(a.id);
      return;
    }
    case 'assistant_message':
      appendInboundMessage(agentId, msg, msg.payload.data, set, get, 'assistant');
      // Collapse the assistant streaming preview now that the canonical block arrived.
      collapseKind(agentId, 'assistant', set);
      return;
    case 'tool_call':
      appendInboundMessage(agentId, msg, msg.payload.data, set, get, 'toolCall');
      collapseToolCallFinalize(agentId, msg.payload.data.toolUseId, set);
      return;
    case 'tool_result':
      appendInboundMessage(agentId, msg, msg.payload.data, set, get, 'toolResult');
      return;
    case 'thinking':
      appendInboundMessage(agentId, msg, msg.payload.data, set, get, 'thinking');
      collapseKind(agentId, 'thinking', set);
      return;
    case 'status': {
      const data = msg.payload.data;
      const isIdle = data.state === 'idle';
      // Backstop: when the turn ends, no in-flight delta can legitimately
      // exist. Clear streaming preview + finalized set so any orphan that
      // slipped past the per-toolUseId collapse (e.g. unmatched parallel
      // block, late delta after rAF reorder) doesn't persist until reload.
      if (isIdle) flushPendingDeltas();
      set((s) => {
        const chats = { ...s.chats };
        const chat = ensureChat({ ...s, chats }, agentId);
        chats[agentId] = isIdle
          ? { ...chat, status: data, streamingBlocks: {}, finalizedToolUseIds: new Set() }
          : { ...chat, status: data };
        return { chats };
      });
      return;
    }
    case 'rate_limit': {
      const data = msg.payload.data;
      set((s) => {
        const chats = { ...s.chats };
        const chat = ensureChat({ ...s, chats }, agentId);
        chats[agentId] = { ...chat, rateLimit: data };
        return { chats };
      });
      return;
    }
    case 'error':
      set((s) => {
        const chats = { ...s.chats };
        const chat = ensureChat({ ...s, chats }, agentId);
        chats[agentId] = { ...chat, lastError: msg.payload.kind === 'error' ? msg.payload.data : null };
        return { chats };
      });
      return;
    case 'reaction':
      handleReaction(agentId, msg.payload.data, set);
      return;
    case 'question':
      handleQuestion(agentId, msg.payload.data, set);
      return;
    case 'assistant_message_delta':
      handleStreamingDelta(agentId, 'assistant', msg.payload.data, set);
      return;
    case 'thinking_delta':
      handleStreamingDelta(agentId, 'thinking', msg.payload.data, set);
      return;
    case 'tool_call_delta':
      handleStreamingDelta(agentId, 'toolCall', msg.payload.data, set);
      return;
    case 'user_message': {
      const data = msg.payload.data;

      // Swift-style adoption: if this is a server-stamped echo (niu- prefix),
      // find the local optimistic entry by text match + timestamp proximity
      // and migrate its id to the server id. More robust than hash matching
      // because it doesn't depend on agentId equality.
      if (msg.id.startsWith('niu-')) {
        const serverTs = niuIdTimestamp(msg.id) ?? Date.now();
        let adopted = false;
        set((s) => {
          // Search across all chats — the echo might route to a different agentId
          // than we stored (e.g. server resolves the agent selector).
          for (const [chatAgentId, chat] of Object.entries(s.chats)) {
            const localIdx = chat.history.messages.findIndex((m) => {
              if (m.id.startsWith('niu-')) return false;
              if (m.payload.kind !== 'user_message') return false;
              if (m.payload.data.text !== data.text) return false;
              const localTs = chat.history.timestamps[m.id];
              return localTs !== undefined && Math.abs(serverTs - localTs) < 300_000;
            });
            if (localIdx === -1) continue;

            adopted = true;
            const local = chat.history.messages[localIdx]!;
            const oldId = local.id;
            const preservedTs = chat.history.timestamps[oldId] ?? serverTs;

            const newMessages = [...chat.history.messages];
            newMessages[localIdx] = { ...local, id: msg.id };
            const newTimestamps = { ...chat.history.timestamps };
            delete newTimestamps[oldId];
            newTimestamps[msg.id] = preservedTs;
            const newDelivery = { ...chat.history.deliveryStatus };
            delete newDelivery[oldId];
            newDelivery[msg.id] = 'sent';

            const history: ChatHistory = {
              ...chat.history,
              messages: newMessages,
              timestamps: newTimestamps,
              deliveryStatus: newDelivery,
            };
            const chats = { ...s.chats, [chatAgentId]: { ...chat, history } };

            // Clean up inflightOutbound entry (if any).
            const inflightKey = userMessageHash(data);
            const next = new Map(s.inflightOutbound);
            next.delete(inflightKey);
            scheduleSave(chatAgentId, history);
            return { chats, inflightOutbound: next };
          }
          return s;
        });
        if (adopted) return;
      }

      // Fallback: hash-based match (handles sibling devices that send the same text,
      // or non-niu-prefixed echoes).
      const inflightKey = userMessageHash(data);
      const inflight = get().inflightOutbound.get(inflightKey);
      if (inflight && inflight.agentId === agentId) {
        set((s) => {
          const chats = { ...s.chats };
          const chat = chats[agentId];
          if (!chat) return s;
          const history: ChatHistory = {
            ...chat.history,
            deliveryStatus: { ...chat.history.deliveryStatus, [inflight.clientId]: 'sent' },
          };
          chats[agentId] = { ...chat, history };
          const next = new Map(s.inflightOutbound);
          next.delete(inflightKey);
          scheduleSave(agentId, history);
          return { chats, inflightOutbound: next };
        });
      } else {
        appendInboundMessage(agentId, msg, data, set, get, 'sibling');
      }
      return;
    }
    case 'catchup_complete':
      // SeqTracker has been updated by the transport; nothing else to do here.
      return;
    case 'earlier_complete': {
      const more = msg.payload.kind === 'earlier_complete' ? msg.payload.data.more : false;
      set((s) => {
        const next: Record<string, AgentChat> = {};
        for (const [aid, c] of Object.entries(s.chats)) {
          next[aid] = { ...c, loadingOlder: false, reachedOldestRestored: c.reachedOldestRestored || !more };
        }
        return { chats: next };
      });
      return;
    }
    case 'devices_list':
      set(() => ({ knownDevices: (msg.payload.data as { devices: DeviceInfo[] }).devices, devicesLoading: false }));
      return;
    case 'inline_snippet': {
      const data = msg.payload.data;
      set((s) => {
        const chats = { ...s.chats };
        const chat = ensureChat({ ...s, chats }, agentId);
        // A new snippet replaces any prior one — the ChatView keeps a single
        // pane and just swaps the iframe.
        chats[agentId] = { ...chat, inlineSnippet: data };
        return { chats };
      });
      return;
    }
    case 'inline_snippet_reload': {
      const data = msg.payload.data;
      set((s) => {
        const chat = s.chats[agentId];
        if (!chat || chat.inlineSnippet?.token !== data.token) return s;
        return {
          chats: {
            ...s.chats,
            [agentId]: { ...chat, inlineSnippetReloadNonce: chat.inlineSnippetReloadNonce + 1 },
          },
        };
      });
      return;
    }
    case 'inline_snippet_closed': {
      const data = msg.payload.data;
      set((s) => {
        const chat = s.chats[agentId];
        if (!chat || chat.inlineSnippet?.token !== data.token) return s;
        return { chats: { ...s.chats, [agentId]: { ...chat, inlineSnippet: null } } };
      });
      return;
    }
    case 'answer':
    case 'presence':
    case 'catchup':
    case 'fetch_earlier_messages':
    case 'message_batch':
    case 'device_register':
    case 'device_info':
    case 'device_unregister':
    case 'devices_list_request':
    case 'device_delete_request':
    case 'device_rename_request':
      // Outbound or transport-unfolded types — we shouldn't see these here.
      return;
  }
}

function appendInboundMessage(
  agentId: string,
  msg: NiuMessage,
  _payload:
    | AssistantMessagePayload
    | ToolCallPayload
    | ToolResultPayload
    | ThinkingPayload
    | UserMessagePayload,
  set: (fn: (s: Store) => Partial<Store> | Store) => void,
  get: () => Store,
  _kind: 'assistant' | 'toolCall' | 'toolResult' | 'thinking' | 'sibling',
): void {
  void _payload;
  void _kind;
  set((s) => {
    const chats = { ...s.chats };
    const chat = ensureChat({ ...s, chats }, agentId);
    // Dedupe by envelope id (catchup replays / out-of-order live).
    if (chat.history.messages.some((m) => m.id === msg.id)) {
      return { chats };
    }
    // Reorder late aux frames (tool_result/tool_call/thinking) that arrive
    // after the assistant_message they logically precede (protocol §4).
    const insertIdx = reorderIndexForLateAuxFrame(
      msg,
      chat.history.messages,
      chat.history.timestamps,
      chat.history.messages.length,
    );
    const reorderedMessages =
      insertIdx === chat.history.messages.length
        ? [...chat.history.messages, msg]
        : [
            ...chat.history.messages.slice(0, insertIdx),
            msg,
            ...chat.history.messages.slice(insertIdx),
          ];
    const history: ChatHistory = {
      ...chat.history,
      messages: reorderedMessages,
      timestamps: {
        ...chat.history.timestamps,
        [msg.id]: niuIdTimestamp(msg.id) ?? Date.now(),
      },
    };
    chats[agentId] = { ...chat, history };
    scheduleSave(agentId, history);
    // Bump unread if not active.
    let unread = s.unread;
    if (s.activeAgentId !== agentId) {
      unread = { ...unread, [agentId]: (unread[agentId] ?? 0) + 1 };
    }
    return { chats, unread };
  });
  void get;
}

function handleReaction(
  agentId: string,
  data: ReactionPayload,
  set: (fn: (s: Store) => Partial<Store> | Store) => void,
): void {
  set((s) => {
    const chats = { ...s.chats };
    const chat = ensureChat({ ...s, chats }, agentId);
    // The server may send the reaction with either the niu-… echo id (primary,
    // common case for siblings) or the originator's pre-echo local id. Resolve
    // to whichever id this client actually has in its local message log; fall
    // back to data.messageId so out-of-order reactions (frame arrives before
    // the message) still get stored and surface once the message lands.
    const candidates = [data.messageId, ...(data.alsoMatch ?? [])];
    const local = chat.history.messages.find((m) => candidates.includes(m.id));
    const targetId = local?.id ?? data.messageId;
    const reactions = { ...chat.history.reactions };
    const current = new Set(reactions[targetId] ?? []);
    if (data.emoji == null || data.emoji === '') {
      reactions[targetId] = [];
    } else {
      current.add(data.emoji);
      reactions[targetId] = Array.from(current);
    }
    const history: ChatHistory = { ...chat.history, reactions };
    chats[agentId] = { ...chat, history };
    scheduleSave(agentId, history);
    return { chats };
  });
}

function handleQuestion(
  agentId: string,
  data: QuestionPayload,
  set: (fn: (s: Store) => Partial<Store> | Store) => void,
): void {
  set((s) => {
    const chats = { ...s.chats };
    const chat = ensureChat({ ...s, chats }, agentId);
    chats[agentId] = { ...chat, pendingQuestion: data };
    return { chats };
  });
}

// ---------- Streaming delta batching ----------
// Deltas can arrive faster than the browser can paint. Coalesce them into one
// state update per animation frame so React renders at most ~60 Hz instead of
// once per inbound chunk.

interface PendingDelta {
  agentId: string;
  kind: 'assistant' | 'thinking' | 'toolCall';
  messageId: string;
  blockIndex: number;
  chunk: string;
  toolUseId?: string | undefined;
}

const pendingDeltas: PendingDelta[] = [];
let rafHandle: ReturnType<typeof requestAnimationFrame> | null = null;
let pendingSet: ((fn: (s: Store) => Partial<Store> | Store) => void) | null = null;

function scheduleFlush(): void {
  if (rafHandle !== null) return;
  if (typeof requestAnimationFrame === 'function') {
    rafHandle = requestAnimationFrame(() => {
      rafHandle = null;
      tickStreaming();
    });
  } else {
    rafHandle = setTimeout(() => {
      rafHandle = null;
      tickStreaming();
    }, 16) as unknown as number;
  }
}

/** Initial reveal rate (chars/sec) used until at least 2 server arrivals
 *  have been observed. Deliberately conservative so the first chunk doesn't
 *  pop in all at once. */
const INITIAL_REVEAL_CPS = 40;
const MIN_REVEAL_CPS = 25;
const MAX_REVEAL_CPS = 600;
/** We aim slightly above the long-term average so we can drain bursts as
 *  they arrive — the lag floor (below) prevents us from overshooting. */
const REVEAL_RATE_FACTOR = 1.05;
/** Hard cap on how close displayedLen may approach buffer.length while the
 *  block is still streaming. Keeping a small "lag puffer" guarantees we
 *  always have something to reveal during the gaps between server bursts —
 *  which is what makes the chunky/pausey feel disappear. Snapped to full on
 *  collapse so the handoff to the final history bubble is seamless. */
const LAG_FLOOR_CHARS = 6;

function blockRevealCps(b: StreamingBlock, now: number): number {
  const elapsedMs = now - b.startedAt;
  if (b.chunkCount < 2 || elapsedMs < 300) return INITIAL_REVEAL_CPS;
  const observed = (b.buffer.length / elapsedMs) * 1000;
  const target = observed * REVEAL_RATE_FACTOR;
  return Math.max(MIN_REVEAL_CPS, Math.min(target, MAX_REVEAL_CPS));
}

/** Single rAF tick: drain pending deltas, then advance every block's
 *  `displayedLen` toward `buffer.length`. Reschedules itself while either
 *  pending deltas or undisplayed buffer remain. */
let lastTickAt: number | null = null;

function tickStreaming(): void {
  if (pendingSet === null) return;
  const set = pendingSet;
  const drained = pendingDeltas.splice(0, pendingDeltas.length);
  const now = Date.now();
  const dtMs = lastTickAt === null ? 16 : Math.max(1, Math.min(now - lastTickAt, 100));
  lastTickAt = now;

  set((s) => {
    const chats = { ...s.chats };
    const touched = new Set<string>();

    // 1) Apply pending deltas (extend buffers + record arrival timestamps).
    for (const d of drained) {
      const chat = ensureChat({ ...s, chats }, d.agentId);
      // Drop late tool_call deltas whose complete frame already finalized
      // this turn (see flushPendingDeltas for rationale).
      if (
        d.kind === 'toolCall' &&
        d.toolUseId !== undefined &&
        chat.finalizedToolUseIds.has(d.toolUseId)
      ) {
        continue;
      }
      const base = touched.has(d.agentId) ? chats[d.agentId]!.streamingBlocks : chat.streamingBlocks;
      const blocks = appendDelta(base, d.kind, d.messageId, d.blockIndex, d.chunk, d.toolUseId);
      chats[d.agentId] = { ...(touched.has(d.agentId) ? chats[d.agentId]! : chat), streamingBlocks: blocks };
      touched.add(d.agentId);
    }

    // 2) Advance displayedLen on every block at its observed rate.
    for (const agentId of Object.keys(chats)) {
      const chat = chats[agentId]!;
      const blocks = chat.streamingBlocks;
      let mutated: Record<string, StreamingBlock> | null = null;
      for (const k of Object.keys(blocks)) {
        const b = blocks[k]!;
        const backlog = b.buffer.length - b.displayedLen;
        if (backlog <= 0) continue;
        // Mid-stream: never reveal the last LAG_FLOOR chars. They'll be
        // shown by the final history bubble after collapse.
        const maxAdvance = Math.max(0, backlog - LAG_FLOOR_CHARS);
        if (maxAdvance <= 0) continue;
        const cps = blockRevealCps(b, now);
        const accumulated = b.revealResidual + (cps * dtMs) / 1000;
        const wantedAdvance = Math.floor(accumulated);
        if (wantedAdvance <= 0) {
          if (accumulated === b.revealResidual) continue;
          if (!mutated) mutated = { ...blocks };
          mutated[k] = { ...b, revealResidual: accumulated };
          continue;
        }
        const advance = Math.min(wantedAdvance, maxAdvance);
        // If we hit the lag floor, drop the residual so it doesn't burst out
        // when the floor next recedes.
        const residual = advance < wantedAdvance ? 0 : accumulated - wantedAdvance;
        if (advance <= 0) {
          if (residual === b.revealResidual) continue;
          if (!mutated) mutated = { ...blocks };
          mutated[k] = { ...b, revealResidual: residual };
          continue;
        }
        const next = b.displayedLen + advance;
        if (!mutated) mutated = { ...blocks };
        mutated[k] = { ...b, displayedLen: next, revealResidual: residual };
      }
      if (mutated) {
        chats[agentId] = { ...chat, streamingBlocks: mutated };
      }
    }

    return { chats };
  });

  // 3) If anything is still in flight, schedule another tick.
  const state = pendingSet === null ? null : (useChatStore.getState() as ChatStoreState);
  let stillBusy = pendingDeltas.length > 0;
  if (!stillBusy && state) {
    outer: for (const agentId of Object.keys(state.chats)) {
      const chat = state.chats[agentId]!;
      for (const k of Object.keys(chat.streamingBlocks)) {
        const b = chat.streamingBlocks[k]!;
        // Only reschedule while we still have room above the lag floor —
        // once we've reached it, sit idle until the next chunk or collapse.
        if (b.buffer.length - b.displayedLen > LAG_FLOOR_CHARS) {
          stillBusy = true;
          break outer;
        }
      }
    }
  }
  if (stillBusy) scheduleFlush();
}

/** Synchronous flush: drain deltas AND snap every block's displayedLen up to
 *  buffer.length. Used by collapse / disconnect / agent switch so no in-flight
 *  state can resurrect a block we're about to drop. */
function flushPendingDeltas(): void {
  if (rafHandle !== null) {
    if (typeof cancelAnimationFrame === 'function') cancelAnimationFrame(rafHandle as number);
    else clearTimeout(rafHandle as unknown as ReturnType<typeof setTimeout>);
    rafHandle = null;
  }
  if (pendingSet === null) return;
  const drained = pendingDeltas.splice(0, pendingDeltas.length);
  if (drained.length === 0) return;
  const set = pendingSet;
  set((s) => {
    const chats = { ...s.chats };
    const touched = new Set<string>();
    for (const d of drained) {
      const chat = ensureChat({ ...s, chats }, d.agentId);
      // Drop late tool_call deltas whose complete frame already finalized
      // this turn — appendDelta would otherwise create an orphan block
      // that no future collapse will clean up.
      if (
        d.kind === 'toolCall' &&
        d.toolUseId !== undefined &&
        chat.finalizedToolUseIds.has(d.toolUseId)
      ) {
        continue;
      }
      const base = touched.has(d.agentId) ? chats[d.agentId]!.streamingBlocks : chat.streamingBlocks;
      const blocks = appendDelta(base, d.kind, d.messageId, d.blockIndex, d.chunk, d.toolUseId);
      chats[d.agentId] = { ...(touched.has(d.agentId) ? chats[d.agentId]! : chat), streamingBlocks: blocks };
      touched.add(d.agentId);
    }
    return { chats };
  });
}

function handleStreamingDelta(
  agentId: string,
  kind: 'assistant' | 'thinking' | 'toolCall',
  data: AssistantMessageDeltaPayload | ThinkingDeltaPayload | ToolCallDeltaPayload,
  set: (fn: (s: Store) => Partial<Store> | Store) => void,
): void {
  pendingSet = set;
  const chunk = 'inputDelta' in data ? data.inputDelta : data.delta;
  const toolUseId = 'toolUseId' in data ? data.toolUseId : undefined;
  pendingDeltas.push({
    agentId,
    kind,
    messageId: data.messageId,
    blockIndex: data.blockIndex,
    chunk,
    toolUseId,
  });
  scheduleFlush();
}

function collapseKind(
  agentId: string,
  kind: 'assistant' | 'thinking' | 'toolCall',
  set: (fn: (s: Store) => Partial<Store> | Store) => void,
): void {
  // Drain any pending deltas first so a late rAF doesn't resurrect a block
  // we're about to collapse.
  flushPendingDeltas();
  set((s) => {
    const chat = s.chats[agentId];
    if (!chat) return s;
    const blocks = collapseStreaming(chat.streamingBlocks, kind);
    if (blocks === chat.streamingBlocks) return s;
    return { chats: { ...s.chats, [agentId]: { ...chat, streamingBlocks: blocks } } };
  });
}

/** Tool-call-precise collapse: drop only blocks sharing this `toolUseId`,
 *  and remember the id so a late tail delta can be dropped instead of
 *  resurrecting an orphan. Replaces `collapseKind(_, 'toolCall')` to keep
 *  parallel sibling tool_calls streaming correctly. */
function collapseToolCallFinalize(
  agentId: string,
  toolUseId: string,
  set: (fn: (s: Store) => Partial<Store> | Store) => void,
): void {
  flushPendingDeltas();
  set((s) => {
    const chat = s.chats[agentId];
    if (!chat) return s;
    const blocks = collapseToolCallByUseId(chat.streamingBlocks, toolUseId);
    const finalized = new Set(chat.finalizedToolUseIds);
    finalized.add(toolUseId);
    return {
      chats: {
        ...s.chats,
        [agentId]: { ...chat, streamingBlocks: blocks, finalizedToolUseIds: finalized },
      },
    };
  });
}

// ---------- Convenience selectors ----------

export function useActiveChat(): AgentChat | undefined {
  return useChatStore((s) => s.chats[s.activeAgentId]);
}

export function useAgentList(): { agents: AgentDescriptor[]; activeId: string; unread: Record<string, number> } {
  return useChatStore(
    (s) => ({ agents: s.agents, activeId: s.activeAgentId, unread: s.unread }),
    shallow,
  );
}

// Suppress unused warning for newUUID re-export readiness.
export { newUUID };
